'use client'
import { alpha, Stack, styled } from '@mui/material'

const StyledWrapper = styled(Stack)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  padding: '1rem 2rem',
  alignItems: 'center',
  background: `url('/images/halftone-background.png') repeat`,
  backgroundSize: '30rem',

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // The veil is the surface, so it has to come from a role. At 0.9 the literal does not tint the
    // halftone underneath - it REPLACES it, so in dark it would stay a near-white field while the ink on
    // top follows `onSurface`: white on white, which is the 1.01 this feature already paid for once.
    //
    // The PNG is opaque and greyscale, a white field at 255 with dots at 0. Composed over the field,
    // `alpha(surface, 0.9)` gives #222B36 in dark and #FAFBFD in light, with the ink on it at 14.32 and
    // 13.42. The texture survives and keeps its polarity - the dots read 1.31 against the field in dark
    // and 1.25 in light - so the image needs no dark variant.
    //
    // That composite is what `roles.ts` names `surfaceVeiled`, and every ink that lands here is declared
    // against it. The role resolves to the nearest primitive on the strict side, so the matrix measures a
    // hair below what the member sees rather than above.
    //
    // What is NOT symmetric is the elevation: the residual tenth of a near-white texture lifts the dark
    // composite 24 levels off the canvas and the light one by 1, so in dark this column reads as a raised
    // surface and in light it reads flush with the page. That is a step that exists only in dark, and it
    // is left for design rather than hidden by raising the alpha, which would erase the texture.
    backgroundColor: alpha(theme.palette.role.surface, 0.9),
    zIndex: 0,
  },

  // Content styles
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },

  [theme.breakpoints.up('lg')]: {
    padding: '0',
    width: '50%',
    background: 'none',
    '&::after': {
      display: 'none',
    },
  },
}))

const StyledImage = styled('div')(({ theme }) => ({
  display: 'none',
  width: '50vw',
  minHeight: '100vh',
  position: 'relative',

  [theme.breakpoints.up('lg')]: {
    display: 'block',
  },
}))

const StyledLogoContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  height: '5.25rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0 2rem',
  [theme.breakpoints.up('lg')]: {
    alignSelf: 'flex-start',
  },
}))

export default function AppLayout({
  children,
}: Readonly<{
  children: React.ReactNode
}>): JSX.Element {
  return (
    <Stack direction='row' minHeight='100vh'>
      <StyledWrapper>
        <StyledLogoContainer>
          <img src='/images/logo.svg' alt='logo' width={0} height={0} style={{ height: '1.75rem', width: 'auto' }} />
        </StyledLogoContainer>
        {children}
      </StyledWrapper>
      <StyledImage>
        <img
          src='/images/login-image.jpg'
          alt='login'
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
      </StyledImage>
    </Stack>
  )
}
